import { useEffect, useState, useCallback } from 'react';
import {
  listCookies,
  upsertCookie,
  deleteCookie,
} from '../lib/api';
import type { CookieRow } from '../lib/types';

interface Props {
  onBack: () => void;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

export function Cookies({ onBack }: Props) {
  const [items, setItems] = useState<CookieRow[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [domain, setDomain] = useState('');
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (filterDomain?: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const list = await listCookies(filterDomain);
      setItems(list);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    if (!domain.trim() || !name.trim() || !value) {
      setError('domain, name, value обязательны');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await upsertCookie({
        domain: domain.trim(),
        name: name.trim(),
        value,
        label: label.trim() || undefined,
      });
      setName('');
      setValue('');
      setLabel('');
      await load(filter.trim() || undefined);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: number): Promise<void> {
    if (!confirm('Удалить cookie?')) return;
    try {
      await deleteCookie(id);
      await load(filter.trim() || undefined);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function applyFilter(): void {
    void load(filter.trim() || undefined);
  }

  const grouped = new Map<string, CookieRow[]>();
  for (const item of items) {
    const arr = grouped.get(item.domain) ?? [];
    arr.push(item);
    grouped.set(item.domain, arr);
  }

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b border-gray-800 px-4 py-3 flex items-center justify-between sticky top-0 bg-gray-950 z-10">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="px-2 py-1 text-gray-400 hover:text-gray-100 hover:bg-gray-800 rounded transition text-sm"
          >
            ← чат
          </button>
          <h1 className="font-semibold tracking-tight truncate">cookies</h1>
        </div>
        <span className="text-xs text-gray-500">
          AES-256-GCM · {items.length}
        </span>
      </header>
      <main className="flex-1 overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          <form
            onSubmit={(e) => void handleSubmit(e)}
            className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3"
          >
            <div className="text-sm text-gray-400">Добавить / обновить</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 outline-none focus:border-gray-500 text-sm"
              />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="session_id"
                className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 outline-none focus:border-gray-500 text-sm"
              />
            </div>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="value (зашифруется AES-256-GCM)"
              rows={3}
              className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 outline-none focus:border-gray-500 text-sm font-mono"
            />
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="label (необязательно)"
              className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 outline-none focus:border-gray-500 text-sm"
            />
            <button
              type="submit"
              disabled={submitting || !domain.trim() || !name.trim() || !value}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-800 disabled:text-gray-500 rounded-lg font-medium transition text-sm"
            >
              {submitting ? '…' : 'сохранить'}
            </button>
          </form>

          <div className="flex gap-2">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyFilter();
              }}
              placeholder="фильтр по домену (Enter)"
              className="flex-1 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 outline-none focus:border-gray-600 text-sm"
            />
            <button
              type="button"
              onClick={applyFilter}
              className="px-3 py-2 bg-gray-800 border border-gray-700 hover:bg-gray-700 rounded-lg text-sm"
            >
              ↻
            </button>
          </div>

          {error && (
            <div className="text-red-400 bg-red-950/40 border border-red-900 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          {loading && (
            <div className="text-gray-500 text-sm text-center py-4">…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="text-gray-500 text-sm text-center py-8">
              cookies не найдены
            </div>
          )}
          {!loading && grouped.size > 0 && (
            <div className="space-y-4">
              {[...grouped.entries()].map(([dom, rows]) => (
                <div
                  key={dom}
                  className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden"
                >
                  <div className="px-3 py-2 border-b border-gray-800 text-sm text-gray-300 font-medium flex items-center justify-between">
                    <span>{dom}</span>
                    <span className="text-gray-500 text-xs">
                      {rows.length}
                    </span>
                  </div>
                  <ul className="divide-y divide-gray-800">
                    {rows.map((c) => (
                      <li
                        key={c.id}
                        className="px-3 py-2 flex items-center justify-between gap-2 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="text-gray-100 font-mono truncate">
                            {c.name}
                          </div>
                          <div className="text-gray-500 text-xs truncate">
                            {c.label ? `${c.label} · ` : ''}upd{' '}
                            {formatTime(c.updated_at)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => void handleDelete(c.id)}
                          className="shrink-0 text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-950/40 transition"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
